/* This script is created and maintained by The Global Chat Team at The Estee Lauder Companies. It is part of a larger ecosystem, so please raise any issues, change or feature requests with our team directly. */

import { scriptLoadError } from "./common-utils";

async function getResourceJson<T>(url: string, errorMessage: string): Promise<T> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(errorMessage);
    }
    return await response.json();
  } catch (error) {
    throw new Error(`${errorMessage}. Cause: ${error}`);
  }
}

function getBrandId(brandName: string): string | null {
  const brandIds: { [key: string]: string | null } = {
    'AramisDesignFragrance': 'ar',
    'Aveda': 'av',
    'Balmain': 'bl',
    'Becca': 'be',
    'BobbiBrown': 'bb',
    'Bumble': 'bu',
    'Clinique': 'cl',
    'Darphin': 'da',
    'DrJart': 'dj',
    'ELCrewards': null,
    'Flirt': 'fl',
    'FredericMalle': 'fm',
    'Genie': 'gn',
    'GlamGlow': 'gg',
    'GrassRoots': 'gr',
    'JoMalone': 'jm',
    'Kilian': 'kl',
    'LabSeries': 'ls',
    'LaMer': 'lm',
    'Lauder': 'el',
    'LeLabo': 'll',
    'Mac': 'mc',
    'Ojon': null,
    'Origins': 'or',
    'Prescriptives': 'pr',
    'Rodin': 'rl',
    'Smashbox': 'sx',
    'TomFordBeauty': 'tf',
    'TooFaced': 'to'
  }

  return brandIds[brandName];
}

function constructBuidFromUtag() {
  if (!window.utag?.data) return null;
  const brandName = window.utag.data.brand;
  const languageCode = window.utag.data.language_code;
  const countryCode = window.utag.data.locale.split("_")[1].toLowerCase();
  const brandId = getBrandId(brandName);

  if (!brandId || !brandName || !languageCode || !countryCode) return null;

  return `2-${brandId}-${languageCode}-${countryCode}-ecommv1`;
}

function getBuid() {
  let buid = null;
  if (window && window.buid) {
    buid = window.buid;
  }
  if (window && window.LiveChat && window.LiveChat.businessUnitId) {
    buid = window.LiveChat.businessUnitId;
  }
  if (
    window &&
    window.utag &&
    window.utag.data &&
    window.utag.data.businessUnitId &&
    typeof window.utag.data.businessUnitId === "string" &&
    window.utag.data.businessUnitId.trim() !== ""
  )
    buid = window.utag.data.businessUnitId;

  if (
    window &&
    window.Drupal &&
    window.Drupal.settings &&
    window.Drupal.settings.elc_ecom &&
    window.Drupal.settings.elc_ecom.businessUnitId &&
    typeof window.Drupal.settings.elc_ecom.businessUnitId === "string" &&
    window.Drupal.settings.elc_ecom.businessUnitId.trim() !== ""
  )
    buid = window.Drupal.settings.elc_ecom.businessUnitId;

  if (
    window &&
    window.utag &&
    window.utag.data &&
    window.utag.data.locale &&
    window.utag.data.language_code &&
    window.utag.data.brand
  )
    buid = constructBuidFromUtag();

  if (!buid) throw new Error(
    "No BUID found. Cannot load LiveChat."
  );

  window.LiveChat!.businessUnitId = buid;
  return buid;
}

function getSiteEnv() {
  if (window.currentEnvironment) {
    return window.currentEnvironment;
  }
  if (window.LiveChat && window.LiveChat.environment) {
    return window.LiveChat.environment;
  }
  console.warn('LIVECHAT: Current environment is not defined. Loading LiveChat as local dev environment.');
  return 'local';
}

function getAssetManagerUrl(buid: string, env: string): string {
  return `https://${env === 'int' ? 'uat.' : ''}ncsa.sdapi.io/stardust-buid-configurations-v1/configurations/${buid}?path=ugc.global_chat`;
}

function verifyConfig(config: unknown): asserts config is TenantConfig {
  if (typeof config !== "object" || config === null) {
    throw new TypeError("Config must be a non-null object");
  }
}

export async function loadAffiliateConfig() {
  const buid = getBuid();
  const siteEnv = getSiteEnv();
  const url = getAssetManagerUrl(buid, siteEnv);
  const config = await getResourceJson(
    url,
    `Failed to fetch affiliate's LiveChat configuration from ${url}`
  );

  if (!config) {
    throw new Error(
      "No initial configuration found in window.LiveChat. Cannot load LiveChat."
    );
  }

  verifyConfig(config);

  return config;
}

export async function loadManifest(filename: string): Promise<Manifest> {
  const manifest = await getResourceJson<Manifest>(
    `/livechat/common/${filename}`,
    "Failed to fetch LiveChat manifest file."
  );
  if (!manifest) {
    throw new Error("Failed to load manifest file. Cannot load LiveChat.");
  }
  return manifest;
}

export async function createAssetLoaderScript(path: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const assetLoaderScript = document.createElement('script');
    assetLoaderScript.src = '/livechat' + path;
    assetLoaderScript.dataset.sourceMap = path + '.map';
    assetLoaderScript.type = 'text/javascript';
    assetLoaderScript.onload = () => resolve();
    assetLoaderScript.onerror = (event) => reject(scriptLoadError(event));
    document.head.appendChild(assetLoaderScript);
  });
}
